import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { isLargeVariance, formatVarianceTitle, LARGE_VARIANCE_LABEL } from '@/utils/varianceDisplay';
import { useTripLogsByShift, TripLogSummary } from '@/components/timesheets/ShiftVehicleLog';

// Attendance status badge colors
const ATTENDANCE_BADGE = {
  on_time: { label: 'On Time', className: 'bg-green-500' },
  late_sign_in: { label: 'Late Sign In', className: 'bg-yellow-500' },
  early_sign_out: { label: 'Early Sign Out', className: 'bg-yellow-500' },
  late_and_early: { label: 'Late & Early', className: 'bg-orange-500' },
  missing_clock_in: { label: 'Missing Clock In', className: 'bg-red-500' },
  missing_clock_out: { label: 'Missing Clock Out', className: 'bg-red-500' },
  missing_both_clocks: { label: 'No Attendance', className: 'bg-red-700' },
  overtime: { label: 'Overtime', className: 'bg-blue-500' },
  attendance_exception: { label: 'Exception', className: 'bg-red-500' },
};

// Approval status badge colors
const APPROVAL_BADGE = {
  pending_approval: { label: 'Pending Approval', className: 'bg-yellow-500 text-black', icon: Clock },
  approved_scheduled: { label: 'Approved', className: 'bg-green-600', icon: CheckCircle },
  approved_adjusted: { label: 'Approved (Adjusted)', className: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected', className: 'bg-red-600', icon: XCircle },
  returned_for_correction: { label: 'Needs Correction', className: 'bg-orange-500', icon: AlertCircle },
  // Legacy support
  DRAFT: { label: 'Draft', className: 'bg-gray-500', icon: Clock },
  SUBMITTED: { label: 'Submitted', className: 'bg-yellow-500 text-black', icon: Clock },
  APPROVED: { label: 'Approved', className: 'bg-green-600', icon: CheckCircle },
  REJECTED: { label: 'Rejected', className: 'bg-red-600', icon: XCircle },
};

const AttendanceBadge = ({ status }) => {
  const config = ATTENDANCE_BADGE[status] || { label: status || '-', className: 'bg-gray-500' };
  return (
    <Badge className={`${config.className} text-white text-xs`}>
      {config.label}
    </Badge>
  );
};

const ApprovalBadge = ({ status }) => {
  const config = APPROVAL_BADGE[status] || { label: status || '-', className: 'bg-gray-500', icon: Clock };
  const Icon = config.icon;
  return (
    <Badge className={`${config.className} text-white text-xs flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

const formatTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
};

const formatMinutes = (mins) => {
  if (!mins && mins !== 0) return '-';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// Actual time worked from attendance (actual_duration_minutes, else clock-in → clock-out), e.g. "7h 45m".
function workedLabel(ts) {
  let mins = Number(ts.actual_duration_minutes) || 0;
  if (!mins && ts.actual_clock_in_time && ts.actual_clock_out_time) {
    mins = Math.round((new Date(ts.actual_clock_out_time) - new Date(ts.actual_clock_in_time)) / 60000);
  }
  if (!(mins > 0)) return null;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

export default function MyTimesheetsV2Page() {
  const { hasPermission } = useAuth();
  const canSeeAmounts = hasPermission('invoices.manage') || hasPermission('payroll.manage') || hasPermission('timesheets.approve') || hasPermission('finance.view');
  // client#11: the worker's own trip logs, matched to each timesheet by shift
  const tripsByShift = useTripLogsByShift(hasPermission('transport.view_own') || hasPermission('transport.log'));
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/timesheets/v2/my');
        setTimesheets(res.data.items || []);
      } catch (e) {
        // Fallback to legacy endpoint
        try {
          const res = await api.get('/timesheets/my');
          setTimesheets(res.data.data || []);
        } catch {
          toast.error('Failed to load timesheets');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-timesheets-v2">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">My Timesheets</h1>
        <p className="text-muted-foreground mt-1">
          View your attendance and timesheet records
        </p>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900" data-testid="timesheet-auto-submit-note">Your timesheet is submitted automatically when you clock out</p>
              <p className="text-sm text-blue-700 mt-1">
                There is no Submit button — clocking out of a shift creates and submits the timesheet based on your scheduled shift times.
                Your actual clock-in and clock-out times (and the time worked) are recorded as attendance evidence.
                An administrator will review and approve your timesheet for payment.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Timesheet History</CardTitle>
          <CardDescription>Your scheduled shifts and attendance records</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Participant</TableHead>
                <TableHead className="text-center">Scheduled</TableHead>
                <TableHead className="text-center">Clock In</TableHead>
                <TableHead className="text-center">Clock Out</TableHead>
                <TableHead>Attendance</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Status</TableHead>
                {canSeeAmounts && <TableHead className="text-right">Amount</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canSeeAmounts ? 9 : 8} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : timesheets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canSeeAmounts ? 9 : 8} className="text-center py-8 text-muted-foreground">
                    No timesheets yet. Complete a shift to see your timesheet records.
                  </TableCell>
                </TableRow>
              ) : timesheets.map(ts => (
                <TableRow key={ts.id} data-testid={`my-timesheet-row-${ts.id}`}>
                  <TableCell className="font-mono text-xs">{ts.date}</TableCell>
                  <TableCell>{ts.participant_name || 'N/A'}</TableCell>
                  <TableCell className="text-center">
                    <span className="font-mono text-xs">
                      {ts.start_time || '-'} - {ts.end_time || '-'}
                    </span>
                    <br />
                    <span className="text-muted-foreground text-xs">
                      ({(ts.base_duration_hours || ts.hours_worked || 0).toFixed(2)}h)
                    </span>
                  </TableCell>
                  <TableCell className="text-center font-mono text-xs">
                    {formatTime(ts.actual_clock_in_time) || '-'}
                    {ts.check_in_notes && (
                      <p className="font-sans text-[11px] text-muted-foreground mt-1 max-w-[160px] mx-auto truncate" title={ts.check_in_notes} data-testid={`my-timesheet-checkin-note-${ts.id}`}>
                        “{ts.check_in_notes}”
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-center font-mono text-xs">
                    {formatTime(ts.actual_clock_out_time) || '-'}
                    {workedLabel(ts) && (   /* #8: show the actual duration, not only the scheduled one */
                      <span className="block text-muted-foreground" data-testid={`my-timesheet-worked-${ts.id}`}>({workedLabel(ts)} worked)</span>
                    )}
                    {ts.check_out_notes && (
                      <p className="font-sans text-[11px] text-muted-foreground mt-1 max-w-[160px] mx-auto truncate" title={ts.check_out_notes} data-testid={`my-timesheet-checkout-note-${ts.id}`}>
                        “{ts.check_out_notes}”
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <AttendanceBadge status={ts.attendance_status} />
                    {ts.late_minutes > 0 && (
                      <p
                        className={`text-xs mt-1 ${isLargeVariance(ts.late_minutes) ? 'text-red-600 font-medium' : 'text-orange-600'}`}
                        title={formatVarianceTitle('Late by', ts.late_minutes)}
                      >
                        {isLargeVariance(ts.late_minutes) ? LARGE_VARIANCE_LABEL : `Late: ${ts.late_minutes}m`}
                      </p>
                    )}
                    {ts.early_leave_minutes > 0 && (
                      <p
                        className={`text-xs ${isLargeVariance(ts.early_leave_minutes) ? 'text-red-600 font-medium' : 'text-orange-600'}`}
                        title={formatVarianceTitle('Early by', ts.early_leave_minutes)}
                      >
                        {isLargeVariance(ts.early_leave_minutes) ? LARGE_VARIANCE_LABEL : `Early: ${ts.early_leave_minutes}m`}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <TripLogSummary trips={tripsByShift[ts.shift_id]} testId={`my-timesheet-vehicle-${ts.id}`} />
                  </TableCell>
                  <TableCell>
                    <ApprovalBadge status={ts.approval_status || ts.status} />
                  </TableCell>
                  {canSeeAmounts && (
                    <TableCell className="text-right font-mono">
                      {ts.approval_status === 'approved_scheduled' || ts.approval_status === 'approved_adjusted' || ts.status === 'APPROVED' ? (
                        <span className="text-green-600">${(ts.total_amount || 0).toFixed(2)}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Status Legend</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge className="bg-yellow-500 text-black text-xs">Pending</Badge>
              <span className="text-muted-foreground">Awaiting approval</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-600 text-white text-xs">Approved</Badge>
              <span className="text-muted-foreground">Ready for payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-600 text-white text-xs">Rejected</Badge>
              <span className="text-muted-foreground">Not approved</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-orange-500 text-white text-xs">Needs Correction</Badge>
              <span className="text-muted-foreground">Requires update</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
